import AxiosFactory from '@/entities/security/AxiosFactory'
import { AxiosResponse } from 'axios'
import {
  GET_PRODUCT_GROUPS_URL,
  GET_UPCOMING_INVOICES_URL,
  GET_USER_SERVICES_URL,
  USER_NOTIFICATIONS_URL,
} from '@/modules/myServices/endpoints'
import ApiErrorFactory from '@/entities/errors/api_errors/ApiErrorsFactory'
import UserNotifications from '@/modules/myServices/entities/UserNotifications'
import { DashboardServicesResponseI, DashboardStatusI } from '@/modules/myServices/types/api/DashboardServices'
import { GET_INVOICE_PDF_URL } from '../invoice/endpoints'

/**
 * `excludeStatuses` is a collection from the start upstream, so it is repeated as
 * `excludeStatuses[]=` rather than sent as one comma-joined value — the single-value spelling
 * is a 422. Hand-built like every other query string in the SPA (`FE-API-07`).
 */
function buildDashboardServicesUrl(excludeStatuses: DashboardStatusI[]): string {
  if (!excludeStatuses.length) return GET_USER_SERVICES_URL

  const query = excludeStatuses.map(status => `excludeStatuses[]=${encodeURIComponent(status)}`).join('&')

  return `${GET_USER_SERVICES_URL}?${query}`
}

export default class ServiceApi {
  static locks = {
    loadDashboardServices: false,
    getUserNotificationsData: false,
  }
  async loadProductGroups() {
    try {
      const response = await AxiosFactory.createAxiosInstance().get(GET_PRODUCT_GROUPS_URL)
      return response.data
    } catch (e) {
      throw ApiErrorFactory.create(e)
    }
  }
  /**
   * The dashboard aggregate. Replaces the five per-slug product calls plus the domain call, so
   * the page costs one WHMCS bootstrap for its services instead of six.
   *
   * Locked like `getUserNotificationsData`: a concurrent second call resolves to `undefined`
   * rather than issuing a request, and the caller keeps whatever it already has.
   *
   * @param excludeStatuses Billing statuses the backend leaves out of every section, sent as
   * the repeated `excludeStatuses[]` parameter. Empty asks for the unfiltered dashboard, which
   * is byte-for-byte what the endpoint answered before the filter existed.
   */
  async loadDashboardServices(
    excludeStatuses: DashboardStatusI[] = [],
  ): Promise<DashboardServicesResponseI | undefined> {
    if (ServiceApi.locks.loadDashboardServices) return
    ServiceApi.locks.loadDashboardServices = true
    try {
      const response = await AxiosFactory.createAxiosInstance().get(buildDashboardServicesUrl(excludeStatuses))
      return response.data
    } catch (e) {
      throw ApiErrorFactory.create(e)
    } finally {
      ServiceApi.locks.loadDashboardServices = false
    }
  }
  async loadUpcomingInvoices() {
    try {
      const response = await AxiosFactory.createAxiosInstance().get(GET_UPCOMING_INVOICES_URL)
      return response.data
    } catch (e) {
      throw ApiErrorFactory.create(e)
    }
  }
  async loadInvoicePdf(id: number) {
    try {
      await AxiosFactory.createAxiosInstance()
        .get(`${GET_INVOICE_PDF_URL}/${id}/pdf`)
        .then((res: AxiosResponse) => {
          const bytes = atob(res.data.pdfdata)
          const byteNumbers = new Array(bytes.length)
          for (let i = 0; i < bytes.length; i++) {
            byteNumbers[i] = bytes.charCodeAt(i)
          }
          const pdfByteArray = new Uint8Array(byteNumbers)
          const pdfBlob = new Blob([pdfByteArray], { type: 'application/pdf' })
          const url = window.URL.createObjectURL(pdfBlob)
          const linkElement = document.createElement('a')
          linkElement.href = url
          linkElement.setAttribute('download', res.data.filename)
          document.body.appendChild(linkElement)
          linkElement.click()
        })
    } catch (e) {
      throw ApiErrorFactory.create(e)
    }
  }
  async getUserNotificationsData() {
    if (ServiceApi.locks.getUserNotificationsData) return
    ServiceApi.locks.getUserNotificationsData = true
    try {
      const response = await AxiosFactory.createAxiosInstance().get(USER_NOTIFICATIONS_URL)
      return new UserNotifications(response.data)
    } catch (e) {
      throw ApiErrorFactory.create(e)
    } finally {
      ServiceApi.locks.getUserNotificationsData = false
    }
  }
}
